import React, { FC, Ref, useCallback, useMemo, useState } from 'react';
import { v4 } from 'uuid';
import { useDrag, useDrop } from 'react-dnd';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { Box, Collapse, SelectChangeEvent, Typography } from '@mui/material';
import { CoreLinkButton } from '../../_core/core-button';
import { Select, TextField } from '../../inputs';
import {
	AttendeeRegistrationFieldTypeEnum,
	AttendeeRegistrationFieldTypeLabelEnum,
	Colors,
	FieldTypeWithOptionsList,
	maxFieldOptions,
	minFieldOptions,
} from '../../../constants';
import {
	CoreConditionalSelect,
	CoreFormControl,
	CoreRadio,
	CoreRadioGroup,
	CoreSelectMenuItem,
	CoreSwitch,
	CoreTooltip,
	Option,
	RadioFormLabel,
} from '../../_core';
import { AttendeeRegistrationFieldOption } from './attendee-registration-field-option.component';
import { useCustomRegister } from '../../../hooks';
import { AttendeeRegistrationFormDto, TiersTypeEnum } from '../../../models/dto';
import { DragIcon, PlusIcon } from '../../../icons';
import { HelpIcon } from '../../../icons/HelpIcon';
import { Card } from '../../card';
import { defaultSelectInputStyles } from '../../inputs/input.styles';
import { CollapseTriggerWithIcon } from '../../collapse-trigger-with-icon/collapse-trigger-with-icon.component';
import { Showable } from '../../utils';
import { clsx } from '../../../utils';
import { dragAndDropIconStyles } from './questionnaire-modal.styles';
import { QuestionnaireModalTypeEnum } from '../';

export const DragAndDropQuestionnaireCardType = 'questionnaire-card';

interface DragItem {
	name: string;
	index: number;
}

export interface AttendeeRegistrationFieldProps {
	index: number;
	questionnaireModalType: QuestionnaireModalTypeEnum;
	questionnaireImages: Record<AttendeeRegistrationFieldTypeEnum, string>;
	tiersOptions?: Option[];
	canDrag?: boolean;
	disabled?: boolean;
	shouldExpandAdvancedSettings?: boolean;
	onRemove(): void;
	onMove(index: number, nexIndex: number): void;
}

export const AttendeeRegistrationField: FC<AttendeeRegistrationFieldProps> = (props) => {
	const {
		index,
		tiersOptions = [],
		questionnaireImages,
		disabled,
		canDrag,
		shouldExpandAdvancedSettings = false,
		questionnaireModalType,
		onMove,
		onRemove,
	} = props;

	const form = useFormContext<AttendeeRegistrationFormDto>();
	const {
		watch,
		control,
		setValue,
		formState: { errors: attendeeRegistrationFormErrors },
	} = form;

	const [isAdvancedSettingsVisible, setIsAdvancedSettingsVisible] = useState(shouldExpandAdvancedSettings);

	const { register } = useCustomRegister(form);

	const type = watch(`fields.${index}.type`);
	const name = watch(`fields.${index}.question`);
	const tiersType = watch(`fields.${index}.tiersType`);
	const errors = useMemo(
		() => attendeeRegistrationFormErrors?.fields?.[index],
		[attendeeRegistrationFormErrors, index]
	);

	const { fields, append, swap, remove } = useFieldArray({
		name: `fields.${index}.options`,
		control,
		keyName: 'key',
	});

	const addOption = useCallback((): void => {
		append({ id: v4(), name: '' });
	}, [append]);

	const onChangeType = useCallback(
		(e: SelectChangeEvent<unknown>): void => {
			const newType = e.target.value as AttendeeRegistrationFieldTypeEnum;
			setValue(`fields.${index}.type`, newType);

			remove();

			if (FieldTypeWithOptionsList.includes(newType)) {
				addOption();
				addOption();
			}
		},
		[setValue, index, remove, addOption]
	);

	const removeOption = useCallback(
		(index: number): void => {
			remove(index);
		},
		[remove]
	);

	const [{ opacity }, drag, preview] = useDrag(
		() => ({
			type: DragAndDropQuestionnaireCardType,
			item: { name, index },
			canDrag: canDrag && !disabled,
			collect: monitor => ({
				opacity: monitor.isDragging() ? 0 : 1,
			}),
		}),
		[name, index]
	);
	const [, drop] = useDrop<DragItem>(
		() => ({
			accept: DragAndDropQuestionnaireCardType,
			hover: item => {
				const { name: draggedId, index: draggedIndex } = item;

				if (draggedId !== name) {
					onMove(draggedIndex, index);
					item.index = index;
				}
			},
		}),
		[onMove, name, index]
	);

	return (
		<Card
			sx={{
				padding: '16px 24px',
				gap: '12px',
			}}
			onDelete={onRemove}
			ref={(node: HTMLDivElement) => { drop(preview(node)) }}
		>
			<Showable show={canDrag}>
				<Box ref={drag as unknown as Ref<unknown>} sx={clsx(dragAndDropIconStyles, { cursor: canDrag && !disabled ? 'pointer' : 'default' })}>
					<DragIcon />
				</Box>
			</Showable>

			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: '16px',
				}}
			>
				<Box display="flex" alignItems="center" gap="2px">
					<TextField
						{...register(`fields.${index}.question`, {
							onChange: e => {
								setValue(`fields.${index}.question`, e.target.value);
							},
						})}
						onBlur={() => {
							if (errors?.question) {
								form.trigger(`fields.${index}.question`);
							}
						}}
						placeholder={`Question ${index + 1}`}
						controlSx={{ flex: 1 }}
						multiline
						minRows={1}
						maxRows={4}
						requiredMark
						sx={{
							'&.MuiInputBase-multiline': {
								p: '10px 16px',
								borderRadius: '8px 0 0 8px',

								'& .MuiOutlinedInput-notchedOutline': {
									borderRadius: '8px 0 0 8px',
								}
							}
						}}
						helperText={null}
					/>

					<Select
						{...register(`fields.${index}.type` as any, {
							onChange: onChangeType,
						})}
						placeholder="Select type of answer"
						requiredMark
						controlSx={{ width: '172px', height: '100%' }}
						helperText={null}
						sx={{
							'&.MuiInputBase-root': {
								height: '100%',
								borderRadius: '0 8px 8px 0',

								'& .MuiOutlinedInput-notchedOutline': {
									borderRadius: '0 8px 8px 0',
								}
							}
						}}
						options={Object.values(AttendeeRegistrationFieldTypeEnum).map((value) => ({
							value,
							label: AttendeeRegistrationFieldTypeLabelEnum[value],
						}))}
						renderValue={(value: unknown) =>
							AttendeeRegistrationFieldTypeLabelEnum[value as AttendeeRegistrationFieldTypeEnum]
						}
						renderOption={option => (
							<CoreSelectMenuItem
								key={option.value}
								value={option.value}
								customContainer
								sx={{
									py: '12px',
								}}
							>
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'row',
										alignItems: 'center',
										gap: '8px',
										justifyContent: 'space-between',
										flex: 1,
									}}
								>
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											gap: '2px',
										}}
									>
										<Typography
											sx={{
												fontFamily: 'Montserrat, sans-serif',
												fontSize: '15px',
												fontWeight: '600 !important',
												lineHeight: '21px',
											}}
										>
											{AttendeeRegistrationFieldTypeLabelEnum[option.value as AttendeeRegistrationFieldTypeEnum]}
										</Typography>
										<Typography
											sx={{
												fontFamily: 'Montserrat, sans-serif',
												fontSize: '13px',
												fontWeight: '400',
												lineHeight: '21px',
											}}
										>
											{((value: string) => {
												switch (value as AttendeeRegistrationFieldTypeEnum) {
													case AttendeeRegistrationFieldTypeEnum.TextInput:
														return 'e.g. What’s your IG handle?';
													case AttendeeRegistrationFieldTypeEnum.RadioButtons:
														return 'e.g. How did you hear about us?';
													case AttendeeRegistrationFieldTypeEnum.CheckBox:
														return 'e.g. How did you hear about us?';
													case AttendeeRegistrationFieldTypeEnum.CheckBoxList:
														return 'e.g. What are your primary fitness goals?';
													case AttendeeRegistrationFieldTypeEnum.DropDown:
														return 'e.g. How did you hear about us?';
													case AttendeeRegistrationFieldTypeEnum.DatePicker:
														return 'e.g. What’s your date of birth?';
												}
											})(option.value)}
										</Typography>
									</Box>
									<Box
										sx={{
											borderRadius: '4px',
											border: `1px solid ${Colors.white_a10}`,
											minWidth: '140px',
											minHeight: '74px',
											background: questionnaireImages?.[option.value as AttendeeRegistrationFieldTypeEnum]
												? `url(${questionnaireImages?.[option.value as AttendeeRegistrationFieldTypeEnum]})`
												: Colors.white_a5,
										}}
									></Box>
								</Box>
							</CoreSelectMenuItem>
						)}
					/>
				</Box>

				{FieldTypeWithOptionsList.includes(type) ? (
					<>
						<CoreFormControl requiredMark fullWidth>
							{fields.map((field, optionIndex) => (
								<AttendeeRegistrationFieldOption
									key={field.id}
									id={field.id as string}
									index={optionIndex}
									fieldIndex={index}
									type={type}
									disabledRemoveButton={fields.length <= minFieldOptions}
									onRemove={() => {
										removeOption(optionIndex);
									}}
									onMove={swap}
								/>
							))}
						</CoreFormControl>
						{fields.length < maxFieldOptions ? (
							<CoreLinkButton onClick={addOption}>
								<PlusIcon />
								<Typography
									sx={{
										fontFamily: 'Montserrat, sans-serif',
										fontSize: '15px',
										fontWeight: 500,
									}}
								>
									Add Option
								</Typography>
							</CoreLinkButton>
						) : null}
					</>
				) : null}
			</Box>

			<Box>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: '24px',
					}}
				>
					<CoreSwitch
						{...register(`fields.${index}.required` as any, {
							type: 'checkbox',
						})}
						onChange={(_event, checked: boolean) => {
							setValue(`fields.${index}.required`, checked);
						}}
						label="Required"
						labelSx={{
							fontFamily: 'Montserrat, sans-serif',
							fontSize: '15px',
							fontWeight: '500',
							lineHeight: '21px',
						}}
					/>

					<Showable show={questionnaireModalType === QuestionnaireModalTypeEnum.Event}>
						<CollapseTriggerWithIcon
							isActivated={isAdvancedSettingsVisible}
							setIsActivated={setIsAdvancedSettingsVisible}
						/>
					</Showable>
				</Box>

				<Showable show={questionnaireModalType === QuestionnaireModalTypeEnum.Event}>
					<Collapse in={isAdvancedSettingsVisible}>
						<Box mt="16px" display="flex" flexDirection="column" gap="16px">
							<Box display="flex" alignItems="center" gap="6px">
								<CoreSwitch
									{...register(`fields.${index}.isCommonForAllTickets` as any, {
										type: 'checkbox',
										transformValue: (v?: boolean) => !v,
									})}
									onChange={(_event, checked: boolean) => {
										setValue(`fields.${index}.isCommonForAllTickets`, !checked);
									}}
									label="Show for each ticket"
									labelSx={{
										fontFamily: 'Montserrat, sans-serif',
										fontSize: '15px',
										fontWeight: '500',
										lineHeight: '21px',
									}}
								/>
								<CoreTooltip
									dark
									arrow
									placement="top"
									title="If an attendee buys several tickets, we show a registration form for each one."
									tooltipSx={{
										backgroundColor: Colors['222222'],
										maxWidth: '264px',
										textAlign: 'center',
										p: '12px',
									}}
									arrowSx={{
										backgroundColor: Colors['222222'],
									}}
								>
									<HelpIcon style={{ width: '14px', height: '14px', color: Colors.white }} />
								</CoreTooltip>
							</Box>
							{tiersOptions.length ? (
								<Box display="flex" flexDirection="column" gap="4px">
									<CoreRadioGroup
										{...register(`fields.${index}.tiersType`, {
											type: 'radio',
										})}
										onChange={(_e, value) => {
											setValue(`fields.${index}.tiersType`, value as TiersTypeEnum);
											setValue(`fields.${index}.tierIds`, value === 'selected' ? [] : undefined);
										}}
										row
										label="Applicable to:"
										controlSx={{
											'& .MuiFormControlLabel-root:not(:last-child)': {
												marginRight: '24px',
											},
										}}
									>
										<RadioFormLabel
											label="All Tiers"
											control={<CoreRadio />}
											value={TiersTypeEnum.All}
											sx={{
												'& .MuiFormControlLabel-label': {
													fontWeight: '500',
													fontSize: '15px',
													lineHeight: '21px',
												},
											}}
										/>
										<RadioFormLabel
											label="Select Tiers"
											control={<CoreRadio />}
											value={TiersTypeEnum.Selected}
											sx={{
												'& .MuiFormControlLabel-label': {
													fontWeight: '500',
													fontSize: '15px',
													lineHeight: '21px',
												},
											}}
										/>
									</CoreRadioGroup>
									{tiersType === TiersTypeEnum.Selected ? (
										<CoreConditionalSelect
											{...register(`fields.${index}.tierIds`, {
												ignoreOriginalOnChange: true,
												changePropKey: 'onValueSelected',
												onBlur: () => {
													if (errors?.tierIds) {
														form.trigger(`fields.${index}.tierIds`);
													}
												},
												onChange: (values: string[]) => {
													setValue(`fields.${index}.tierIds`, values);
												},
											})}
											multiple
											options={tiersOptions}
											placeholder="Select at least one tier"
											helperText={null}
											sx={defaultSelectInputStyles()}
										/>
									) : null}
								</Box>
							) : null}
						</Box>
					</Collapse>
				</Showable>
			</Box>
		</Card>
	);
};
